/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DataGridPro,
  GridColDef,
  GridRowParams,
  GridPinnedColumnFields,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useUser } from "../../context/UserContext";
import { GetAllFilesAndFoldersByUserDto } from "../../types/FolderPermission";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Grid2,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import {
  GLOBALSETTINGS,
  GRIDCOLUMNKEYS,
  ModuleNames,
  SEARCHBOX,
  SEVERITIES,
  dataGridHeaderHeight,
  dataGridRowHeight,
} from "../../data/constants";
import SearchBox from "../../components/form/SearchBox/SearchBox";
import useMenuActions from "../../hooks/useMenuActions";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import EditResource from "../editResource/EditResource";
import { FolderFileType } from "../../data/Enums";
import { useDispatch, useSelector } from "react-redux";
import UploadComponent from "../upload/UploadComponent";
import useFolderList from "../../hooks/useFolderList";
import { setRowSelectedFolder } from "../../redux/rowSelectedSlice";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import "./FolderList.scss";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import ActionsMenu from "../../components/form/MenuAction/ActionsMenu";
import {
  ACTIONITEMVALIDATIONS,
  DragMessages,
  GLOBALMESSAGES,
  NO_ACCESS,
} from "../../constants/messages";
import { useSearchParams } from "react-router-dom";
import ShareResource from "../share/ShareResource";

import ReorderIcon from "@mui/icons-material/Reorder";
import GridViewIcon from "@mui/icons-material/GridView";
import AddIcon from "@mui/icons-material/Add";
import { RootState } from "../../redux/store";
import { areAllRowsReadOnly, getFileExtension, renderSvgIcon } from "../../utils/commonHelper";
import CustomLoadingOverlay from "../../components/ui/CustomLoadingOverlay";
import { deepOrange } from "@mui/material/colors";
import { useMenu } from "../../context/MenuContext";
import CustomDraggableRow, { CustomDraggableRowProps } from "./CustomDraggableRow";
import { useDownload } from "../download/DownloadContext";
import { useToaster } from "../../hooks/useToaster";
import useApi from "../../hooks/useApi";
import { usePersistedColumnVisibility } from "../../hooks/usePersistedColumnVisibility";
import { getDescription } from "../../services/folderService";

const FolderList: FC = () => {
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [hideActions, setHideActions] = useState(false);
  // No default for `actions`: the render below always overrides it from `hideActions`.
  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    GRIDCOLUMNKEYS.FOLDER_LIST
  );
  const [menuAnchors, setMenuAnchors] = useState<{
    [key: number]: HTMLElement | null;
  }>({});
  const [currentRow, setCurrentRow] = useState<GetAllFilesAndFoldersByUserDto | null>(null);
  const [highlightedTarget, setHighlightedTarget] = useState<{
    folderId?: number;
    fileId?: number;
  } | null>(null);
  const showToaster = useToaster();
  const dispatch = useDispatch();
  const { userId } = useUser();
  const hasUrlRunRef = useRef(false);
  const deepLinkHandledRef = useRef(false);
  const deepLinkAttemptsRef = useRef(0);

  const { executeApi } = useApi();

  const apiRef = useGridApiRef();
  const { startDownload } = useDownload();
  const [isFolderComponentOpen, setIsFolderComponentOpen] = useState(false);
  const { folderId, folderName, canEdit } = useSelector((state: RootState) => state.selectedFolder);

  const { permissions } = useMenu();

  const [searchParams, setSearchParams] = useSearchParams();
  const parsedIds = useMemo(() => {
    const fId = searchParams.get("folderId");
    const fileId = searchParams.get("fileId");

    return {
      folderId: fId !== null ? Number(fId) : undefined,
      fileId: fileId !== null ? Number(fileId) : undefined,
    };
  }, [searchParams]);

  const folderIdFromURL = parsedIds.folderId;
  const fileIdFromURL = parsedIds.fileId;

  const clearSignal = useSelector(
    (state: RootState) => state.clearSearchTextSlice.clearSearchSignal,
  );
  const handleMenuClose = useCallback((id: number) => {
    setMenuAnchors((prev) => ({ ...prev, [id]: null }));
  }, []);

  const {
    loading,
    pageState,
    paginationModel,
    setPaginationModel,
    moveFolderOrFile,
    handleOnRowsScrollEnd,
    handleSearchText,
    handleGridScroll,
    scrollContainerRef,
  } = useFolderList(userId, apiRef);

  const { actions, states, setters } = useMenuActions(userId, currentRow, () =>
    handleMenuClose(currentRow?.roleFolderPermissionsId),
  );

  useEffect(() => {
    if (pageState?.data?.length) {
      setHideActions(areAllRowsReadOnly(pageState.data));
    }
  }, [pageState.data]);

  const scrollToFilesSection = () => {
    const targetElement = document.getElementById("all-files");
    if (targetElement) {
      window.scrollTo({
        top: targetElement.offsetTop + 120,
        behavior: "smooth",
      });
    }
  };

  // scrollToIndexes only scrolls far enough to reveal the row, so a target below
  // the viewport lands flush with the bottom edge. Put the highlight at the top
  // of the list instead. The browser clamps scrollTop when the offset runs past
  // the end. Until the grid root is mounted, fall back to scrollToIndexes.
  const scrollHighlightedRowIntoView = (rowIndex: number) => {
    const api = apiRef.current;
    if (!api) return;

    if (!api.rootElementRef?.current) {
      api.scrollToIndexes({ rowIndex });
      return;
    }

    api.scroll({ top: rowIndex * dataGridRowHeight });
  };

  // Global Search can point at a new target while this component stays mounted
  // (FolderLayout keeps FolderList mounted across /Home and /listDetails), so
  // the two one-shot guards below must be released for each new deep link.
  // Without this the first jump of a session consumed them and every later jump
  // highlighted the row but never scrolled it into view (Bug 79891).
  useEffect(() => {
    deepLinkHandledRef.current = false;
    deepLinkAttemptsRef.current = 0;
  }, [folderIdFromURL, fileIdFromURL]);

  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    if (!fileIdFromURL && !folderIdFromURL) return;
    if (loading) return; // wait until data load completes

    if (!pageState?.data?.length) return;

    if (deepLinkAttemptsRef.current === 0) {
      deepLinkAttemptsRef.current++;
      return; // allow one render cycle before deciding
    }

    // Always drop the deep-link params through the router rather than
    // window.history.replaceState. A raw replaceState leaves useSearchParams()
    // holding the old params, so re-picking the same item from Global Search
    // produced an unchanged location.search and this effect never re-ran.
    const clearDeepLinkParams = () => setSearchParams(new URLSearchParams(), { replace: true });

    const tryFindRow = (isRetry = false) => {
      const row = pageState?.data?.find((r) => {
        const rowFolderId = Number(r.folderId) || 0;
        const rowFileId = Number(r.fileId) || 0;

        if (folderIdFromURL !== undefined) {
          return rowFolderId === folderIdFromURL;
        }

        if (fileIdFromURL !== undefined) {
          return rowFileId === fileIdFromURL;
        }

        return false;
      });

      if (row) {
        deepLinkHandledRef.current = true;

        // Highlight and selection come from state rather than the URL, so the
        // params can be dropped straight away without losing the highlight.
        // Only the grid scroll waits for the rows to render, and it re-resolves
        // the row index at fire time. No clearTimeout cleanup on purpose:
        // clearing the params re-runs this effect, and a cleanup would cancel
        // the scroll that is the whole point of the deep link.
        scrollToFilesSection();
        setHighlightedTarget({ folderId: folderIdFromURL, fileId: fileIdFromURL });
        apiRef?.current?.setRowSelectionModel([row.roleFolderPermissionsId]);
        clearDeepLinkParams();

        setTimeout(() => {
          const rowIndex = apiRef?.current?.getRowIndexRelativeToVisibleRows?.(
            row.roleFolderPermissionsId,
          );

          if (rowIndex !== undefined && rowIndex !== -1) {
            scrollHighlightedRowIntoView(rowIndex);
          }
        }, 150);

        return;
      }

      // The grid only fetches its next page on scroll, so a target past the
      // loaded pages was never found and the user was told they had lost
      // access to a file they can open (Bug 83623). Pull the next page in;
      // this effect re-runs on the new rows. Only from the effect pass itself:
      // the retry timers below close over an older render's pageState.
      if (
        !isRetry &&
        pageState.data.length < pageState.total &&
        paginationModel.page + 1 < Math.ceil(pageState.total / paginationModel.pageSize)
      ) {
        setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }));
        return;
      }

      // ⏳ Not found yet → try again while the loaded rows render
      if (deepLinkAttemptsRef.current < 10) {
        deepLinkAttemptsRef.current++;
        setTimeout(() => tryFindRow(true), 300);
      } else {
        // ❗ After retries → NOW we show NO_ACCESS (real failure)
        deepLinkHandledRef.current = true;

        (async () => {
          const getDescriptionDto = {
            typeId: fileIdFromURL ?? folderIdFromURL,
            isFolder: !!folderIdFromURL,
          };

          const response = await executeApi(getDescription, getDescriptionDto);

          if (response) {
            showToaster(NO_ACCESS(response), SEVERITIES.ERROR);
          }

          // Drop the stale highlight too, so the previously picked row does not
          // stay marked as if it were the item just searched for.
          setHighlightedTarget(null);
          apiRef?.current?.setRowSelectionModel([]);
          clearDeepLinkParams();
        })();
      }
    };

    tryFindRow();
  }, [loading, pageState.data, folderIdFromURL, fileIdFromURL]);

  useEffect(() => {
    if (hasUrlRunRef.current) return;
    if (
      (searchParams.get("shareUser") === "true" || searchParams.get("shareRole") === "true") &&
      pageState.data
    ) {
      let matchedRow: GetAllFilesAndFoldersByUserDto | undefined;

      if (fileIdFromURL > 0) {
        matchedRow = pageState.data.find((row) => row.fileId === fileIdFromURL);
      } else if (folderIdFromURL > 0) {
        matchedRow = pageState.data.find((row) => row.folderId === folderIdFromURL);
      }

      if (matchedRow) {
        setCurrentRow(matchedRow);
        const targetRowId = matchedRow.roleFolderPermissionsId;
        if (targetRowId && apiRef.current) {
          // Scroll to the row using its ID
          const visibleRowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(targetRowId);
          if (visibleRowIndex) {
            scrollHighlightedRowIntoView(visibleRowIndex);
          }
        }

        if (searchParams.get("shareRole") === "true") {
          setters.setShareToRole(true);
        } else {
          setters.setShareToUser(true);
        }
        hasUrlRunRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, pageState.data]);

  useEffect(() => {
    if (
      states.dialogOpen &&
      actions.downloadData.userId === userId &&
      actions.downloadData.folderId
    ) {
      startDownload(actions.downloadData.folderId, actions.downloadData.folderName);
    }
  }, [states.dialogOpen, actions.downloadData, userId]);

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, row: GetAllFilesAndFoldersByUserDto) => {
      event.stopPropagation();
      setMenuAnchors({
        [row.roleFolderPermissionsId]: event.currentTarget,
      });
      setCurrentRow(row);
    },
    [],
  );

  const handleEditClose = () => {
    if (states.publish) {
      setters.setPublish(false);
    } else {
      setters.setEditDialogOpen(false);
    }
  };

  const getMainEditPermission = () => {
    let isMainEditEnabled = false;
    if (permissions && permissions.length > 0) {
      isMainEditEnabled = permissions?.find(
        (permission) => permission.moduleName === ModuleNames.FileExplorer,
      )?.canEdit;
    }
    return isMainEditEnabled;
  };

  const handleShareClose = (displayRoles: boolean) => {
    const params = new URLSearchParams(window.location.search);
    if (displayRoles) {
      setters.setShareToRole(false);
      params.delete("shareRole");
    } else {
      setters.setShareToUser(false);
      params.delete("shareUser");
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    // Replace URL in the address bar without reloading
    window.history.replaceState({}, "", newUrl);
  };

  const handleAddNewClose = () => {
    setters.setAddNew(false);
  };

  const handleRowDoubleClick = (
    paramsOrRow: GridRowParams | { row: GetAllFilesAndFoldersByUserDto },
    event?: React.MouseEvent,
  ) => {
    let row: GetAllFilesAndFoldersByUserDto;
    if ("id" in paramsOrRow && "row" in paramsOrRow) {
      row = paramsOrRow.row as GetAllFilesAndFoldersByUserDto;
    } else {
      row = paramsOrRow.row;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (row.type === FolderFileType[FolderFileType.Folder]) {
      dispatch(setRowSelectedFolder({ parentFolderId: row.folderId }));
    }
  };

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, newView: "grid" | "list") => {
    if (newView !== null) setViewMode(newView);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "Drag",
        headerName: "",
        headerAlign: "center",
        align: "center",
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const canEdit = params.row.canEdit;
          return (
            <Tooltip
              title={canEdit ? DragMessages.DragToMove : DragMessages.DragToMoveDisabled}
              placement="left"
            >
              <div draggable={canEdit} className={`draggable-column ${!canEdit ? "disabled" : ""}`}>
                <DragIndicatorIcon fontSize="small" />
              </div>
            </Tooltip>
          );
        },
      },
      {
        field: "description",
        headerName: "Name",
        minWidth: 250,
        flex: 1,
        // Bug 78034: the Name column must stay visible. Drag has no column menu and Actions is
        // hidden whenever every row is read-only, so if Name could be hidden too the user could
        // hide every column that carries a menu - and now that the selection persists, that state
        // would survive a reload with no way back to "Manage columns".
        hideable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
            {params.row.type === "File" ? (
              renderSvgIcon(getFileExtension(params.row.description), "file-icon")
            ) : (
              <img src="/folder.svg" alt="Folder Icon" />
            )}
            <Tooltip title={params.row.description} placement="bottom-start">
              <Typography variant="body2">{params.row.description}</Typography>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: "type",
        headerName: "Type",
        width: 90,
      },
      {
        field: "createdBy",
        headerName: "Created By",
        width: 250,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center", // vertical align
              justifyContent: "flex-start", // horizontal align
              height: "100%", // ensures full cell height
              gap: 1,
            }}
          >
            <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: deepOrange[400] }}>
              {getInitials(params.value)}
            </Avatar>
            <Tooltip title={params.value} placement="bottom-start">
              <Typography variant="body2">{params.value}</Typography>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: "createdDate",
        headerName: "Created Date",
        width: 120,
        renderCell: (params) => {
          const formattedDate = params.value
            ? format(params.value, GLOBALSETTINGS.DATE_FORMAT)
            : "";
          return (
            <Box className="created-date-cell">
              <Tooltip title={formattedDate} placement="bottom-start">
                <Typography variant="body2" noWrap>
                  {formattedDate}
                </Typography>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 100,
        flex: 0.3,
        sortable: false,
        hideable: false,
        filterable: false,
        disableColumnMenu: false,
        renderCell: (params) => (
          <>
            <ActionsMenu
              key={params.row.roleFolderPermissionsId}
              currentRow={params.row}
              menuAnchorEl={menuAnchors[params.row.roleFolderPermissionsId]}
              handleMenuOpen={handleMenuOpen}
              handleMenuClose={() => handleMenuClose(params.row.roleFolderPermissionsId)}
              handleAction={actions.handleAction}
              displayRightSide={false}
            />
          </>
        ),
      },
    ],
    [menuAnchors, handleMenuOpen, handleMenuClose],
  );

  const handleNewClick = () => {
    setIsFolderComponentOpen(true);
  };

  const handleCloseFolderComponent = () => {
    setIsFolderComponentOpen(false);
  };

  function getInitials(name: string | null | undefined): string {
    if (!name) return "";

    return name
      .split(" ")
      .filter((n) => n)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }

  const [pinnedColumns, setPinnedColumns] = React.useState<GridPinnedColumnFields>({
    left: [],
  });

  useEffect(() => {
    setPinnedColumns({
      right: !hideActions ? ["actions"] : [],
    });
  }, [hideActions]);

  return (
    <>
      <Box component="form">
        {<BackdropProgressBar loading={states.loading} />}
        <Box mb={2}>
          <Typography variant="h5" component="div" className="mb-3 card-title">
            {folderName !== "All Files/Folders" && (
              <img src="/folder.svg" alt="Folder Icon" className="folder-icon-with-title" />
            )}{" "}
            <Tooltip title={folderName} placement="top" arrow>
              <span className="breadcrumb-ellipsis folder-title-ellipsis">{folderName}</span>
            </Tooltip>
          </Typography>
        </Box>
        <Grid2 container rowSpacing={2} mb={2} justifyContent="space-between">
          <Grid2 size={{ sm: 2, md: 4 }}>
            <Tooltip title="Create or upload a folder or file" placement="top">
              <span className="d-inline-block">
                <Button
                  variant="contained"
                  className="add-new-btn"
                  disabled={!(getMainEditPermission() && canEdit)}
                  onClick={handleNewClick}
                >
                  <AddIcon /> New
                </Button>{" "}
              </span>
            </Tooltip>
          </Grid2>
          <Grid2 size={{ sm: 10, md: 5 }} className="data-action-area">
            <SearchBox
              searchPlaceholder={SEARCHBOX.FOLDERLIST_PLACEHOLDER}
              onSearch={handleSearchText}
              clearSignal={clearSignal}
            />
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              className="toggle-btn-container"
            >
              <Tooltip title="List View" arrow placement="top">
                <ToggleButton value="list" sx={{ borderRadius: "10px" }}>
                  <ReorderIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Grid View" arrow placement="top">
                <ToggleButton value="grid" sx={{ borderRadius: "10px" }}>
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Box className="view-container"></Box>
          {viewMode === "list" ? (
            <div className="grid-list">
              <DataGridPro
                rowHeight={dataGridRowHeight}
                apiRef={apiRef}
                rows={pageState.data ?? []}
                {...pageState.data}
                columns={columns}
                hideFooterSelectedRowCount={true}
                columnHeaderHeight={dataGridHeaderHeight}
                columnVisibilityModel={{
                  ...columnVisibilityModel,
                  actions: !hideActions,
                }}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                getRowClassName={(params) => {
                  // Same predicate as before, but sourced from state rather
                  // than the URL: the deep-link params are cleared as soon as
                  // they are applied (Bug 79891), so reading them here would
                  // drop the highlight the moment it appeared. Matching on
                  // folderId/fileId rather than the grid row id also keeps
                  // working for a freshly created row, which is still carrying
                  // a temporary negative roleFolderPermissionsId.
                  const isHighlighted =
                    highlightedTarget !== null &&
                    ((highlightedTarget.folderId !== undefined &&
                      Number(params.row.folderId) === highlightedTarget.folderId) ||
                      (highlightedTarget.fileId !== undefined &&
                        Number(params.row.fileId) === highlightedTarget.fileId));

                  const isDraggable = params.row.canEdit;

                  return [
                    isHighlighted ? "highlighted-row" : "",
                    isDraggable ? "draggable-row" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                }}
                rowCount={pageState.total}
                loading={loading}
                slots={{
                  row: CustomDraggableRow,
                  loadingOverlay: CustomLoadingOverlay,
                }}
                slotProps={{
                  row: {
                    moveFolderOrFile,
                   } as CustomDraggableRowProps,
                  columnsManagement: {
                    getTogglableColumns: (cols) =>
                      cols.filter((col) => col.field !== "Drag").map((col) => col.field),
                  },
                }}
                paginationMode="server"
                onRowDoubleClick={handleRowDoubleClick}
                getRowId={(row) => row.roleFolderPermissionsId}
                hideFooterPagination
                onRowsScrollEnd={handleOnRowsScrollEnd}
                localeText={{ noRowsLabel: GLOBALMESSAGES.NOROWSTEXT }}
                pinnedColumns={pinnedColumns}
                onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
                className="pointer-cursor"
              />
            </div>
          ) : (
            <Grid2
              container
              columnSpacing={2}
              rowSpacing={2}
              sx={{ maxHeight: 400, overflowY: "auto" }}
              onScroll={handleGridScroll}
              ref={scrollContainerRef}
            >
              {pageState.data && pageState.data.length > 0 ? (
                pageState.data?.map((row) => (
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={row.roleFolderPermissionsId}>
                    <Card
                      sx={{
                        p: 0,
                        borderRadius: 2,
                        boxShadow: 0,
                        background: "#EDF4E8",
                      }}
                      className="grid-view-card"
                      onDoubleClick={() => handleRowDoubleClick({ row })}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Tooltip title={row.description}>
                            <Typography className="tile-title-text">
                              {row.type === "File" ? (
                                <>{renderSvgIcon(getFileExtension(row.description), "file-icon")}</>
                              ) : (
                                <>
                                  <img
                                    src="/folder.svg"
                                    alt="Folder Icon"
                                    className="folder-icon"
                                  />
                                </>
                              )}{" "}
                              {row.description}
                            </Typography>
                          </Tooltip>

                          {/* Actions Menu */}
                          {!hideActions && (
                            <ActionsMenu
                              key={row.roleFolderPermissionsId}
                              currentRow={row}
                              menuAnchorEl={menuAnchors[row.roleFolderPermissionsId]}
                              handleMenuOpen={handleMenuOpen}
                              handleMenuClose={() =>
                                handleMenuClose(row.roleFolderPermissionsId)
                              }
                              handleAction={actions.handleAction}
                              displayRightSide={false}
                            />
                          )}
                        </Box>

                        <Box className="file-details">
                          <Box className="file-details-left">
                            <Avatar
                              sx={{ width: 28, height: 28, fontSize: 12, bgcolor: deepOrange[400] }}
                            >
                              {getInitials(row.createdBy)}
                            </Avatar>

                            <Typography className="file-details-text" color="text.secondary">
                              {row.createdBy}
                            </Typography>
                          </Box>
                          <Typography className="file-details-text" color="text.secondary">
                            {format(row.createdDate, GLOBALSETTINGS.DATE_FORMAT)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid2>
                ))
              ) : (
                <Grid2
                  size={{ xs: 12 }}
                  sx={{
                    height: "350px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: (theme) => alpha(theme.palette.grey[100], 0.38),
                  }}
                >
                  <Box sx={{ textAlign: "center", py: 5, width: "100%" }}>
                    <Typography color="black">{GLOBALMESSAGES.NOROWSTEXT}</Typography>
                  </Box>
                </Grid2>
              )}
            </Grid2>
          )}
        </Grid2>
        <Grid2>
          {isFolderComponentOpen && (
            <UploadComponent
              parentFolderId={folderId}
              onClose={handleCloseFolderComponent}
              fromGrid={false}
            />
          )}
        </Grid2>
        <Grid2>
          <ConfirmationDialog
            open={states.deleteConfirm}
            title={ACTIONITEMVALIDATIONS.CONFIRM}
            message={ACTIONITEMVALIDATIONS.DELETE_RESOURCE}
            onClose={actions.handleDeleteConfirm}
          />
        </Grid2>
        <Grid2>
          <ConfirmationDialog
            open={states.unPublishConfirm}
            title={ACTIONITEMVALIDATIONS.CONFIRM}
            message={ACTIONITEMVALIDATIONS.UNPUBLISH_RESOURCE}
            onClose={actions.handleUnPublishConfirm}
          />
        </Grid2>
        <Grid2>
          {states.editDialogOpen && (currentRow?.folderId || currentRow?.fileId) && (
            <EditResource
              type={currentRow?.type as string}
              typeId={
                currentRow?.type === FolderFileType[FolderFileType.Folder]
                  ? currentRow?.folderId
                  : currentRow?.fileId
              }
              isPublish={false}
              onClose={handleEditClose}
              canManagePermissions={currentRow?.canManagePermissions}
            />
          )}
        </Grid2>
        <Grid2>
          {states.publish && (currentRow?.folderId || currentRow?.fileId) && (
            <EditResource
              type={currentRow?.type as string}
              typeId={
                currentRow?.type === FolderFileType[FolderFileType.Folder]
                  ? currentRow?.folderId
                  : currentRow?.fileId
              }
              isPublish={true}
              onClose={handleEditClose}
              canManagePermissions={currentRow?.canManagePermissions}
            />
          )}
        </Grid2>
        <Grid2>
          {(states.shareToRole || states.shareToUser) &&
            currentRow &&
            (currentRow.folderId || currentRow.fileId) && (
              <ShareResource
                type={currentRow?.type as string}
                typeId={
                  currentRow?.type === FolderFileType[FolderFileType.Folder]
                    ? currentRow?.folderId
                    : currentRow?.fileId
                }
                parentFolderId={currentRow?.parentFolderId}
                displayRoles={states.shareToRole}
                fileUrl={currentRow?.fileUrl}
                description={currentRow?.description}
                onClose={() => handleShareClose(states.shareToRole)}
                canDownload={!!currentRow?.canDownload}
              />
            )}
        </Grid2>
        <Grid2>
          {states.addNew && currentRow?.folderId && (
            <UploadComponent
              parentFolderId={currentRow?.folderId}
              onClose={handleAddNewClose}
              fromGrid={true}
            />
          )}
        </Grid2>
      </Box>
    </>
  );
};

export default FolderList;

 